/**
 * The clubs grid's geometry (clubs redesign handoff §2 and §3).
 *
 * A cover keeps its own orientation, snapped to one of three shapes, and cards go in reading
 * order into whichever column is shortest.
 *
 * Unlike the events wall (components/wall/useMasonry), this layout is recomputed from scratch
 * whenever a cover's ratio lands. The wall is an infinite scroll where a late portrait poster
 * would shove everything below it around; a club list is one page that arrives at once, so the
 * handoff asks for the rebuild instead.
 */

/** Ratios measured off loaded covers, by URL — so switching tabs never re-measures. */
const measured = new Map();

export function rememberRatio(url, ratio) {
  if (!url || !(ratio > 0)) return false;
  if (Math.abs((measured.get(url) || 0) - ratio) < 0.01) return false;
  measured.set(url, ratio);
  return true;
}

/** Everything measured so far, to seed a page's state on mount. */
export const seedRatios = () => Object.fromEntries(measured);

export const SHAPES = {
  portrait: { name: 'portrait', aspect: '4 / 5', h: 1.25 },
  square: { name: 'square', aspect: '1 / 1', h: 1 },
  landscape: { name: 'landscape', aspect: '16 / 9', h: 0.5625 },
};

/**
 * A cover's shape from its natural width / height. A club with no cover — and one whose cover
 * hasn't loaded yet — is square: the handoff's stand-in mix of 4:5 and 16:9 was prototype
 * dressing, and a square tile is the shape that moves least when the real ratio arrives.
 */
export function coverShape(ratio) {
  if (!ratio) return SHAPES.square;
  if (ratio < 0.95) return SHAPES.portrait;
  if (ratio < 1.15) return SHAPES.square;
  return SHAPES.landscape;
}

/** The body under the cover, in card-widths — name, tagline and the member row. */
const BODY_H = 0.62;
/** START YOUR OWN CLUB has no cover; it's about two thirds of a square card. */
export const START_CARD_H = 0.9;

/** A card's height in card-widths, which is all the column packing needs. */
export const cardHeight = (ratio) => coverShape(ratio).h + BODY_H;

/**
 * Reading order into the shortest column, ties to the left.
 * `items` are `{ h, ... }`; the items themselves come back, grouped.
 */
export function columnize(items, cols) {
  const columns = Array.from({ length: Math.max(1, cols) }, () => []);
  const heights = new Array(columns.length).fill(0);
  items.forEach((item) => {
    let pick = 0;
    for (let i = 1; i < heights.length; i += 1) {
      if (heights[i] < heights[pick] - 0.01) pick = i;
    }
    columns[pick].push(item);
    heights[pick] += item.h || 0;
  });
  return columns;
}

/** Columns at a width: 3 on desktop, 2 on tablet (< 980), 1 on phone (< 640). */
export const columnsAt = (width) => (width < 640 ? 1 : width < 980 ? 2 : 3);
